import React, { useEffect, useState } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import './MiddleOfficePages.css';

// Middle office is a 3-tier oversight function, independent from the
// front_office/back_office_verifier/back_office_final deal-workflow chain -
// a user can hold both. Reuses the same authorizer_assignments table/API
// (role = one of these 3 values) rather than a separate mechanism.
const MIDDLE_OFFICE_ROLES = [
  { value: 'middle_office_user', label: 'Middle Office - User' },
  { value: 'middle_office_officer', label: 'Middle Office - Officer' },
  { value: 'middle_office_manager', label: 'Middle Office - Manager' }
];

// Curated: reports (assignable "separately from an admin panel tab", per the
// request) plus the two setup pages that are middle-office-exclusive.
const PAGE_OPTIONS = [
  { value: 'gsec_report', label: 'Report: T Bond' },
  { value: 'tbill_report', label: 'Report: T-Bill' },
  { value: 'gsec_summary_report', label: 'Report: T Bond Summary' },
  { value: 'money-market-report', label: 'Report: Money Market' },
  { value: 'mark-to-market-report', label: 'Report: Mark to Market' },
  { value: 'repo-report', label: 'Report: Repo / Reverse Repo' },
  { value: 'buyback-report', label: 'Report: Buyback' },
  { value: 'sell-transaction-report', label: 'Report: Sell Transaction' },
  { value: 'portfolio-report', label: 'Report: Portfolio' },
  { value: 'counterparty-report', label: 'Report: Counterparty Transaction' },
  { value: 'counterparty-master-report', label: 'Report: Counterparty Detail' },
  { value: 'coupon-maturity-blotter', label: 'Report: Coupon Maturity Blotter' },
  { value: 'counterparty_master', label: 'Setup: Counterparty Master (edit)' },
  { value: 'dealer_limit_setup', label: 'Setup: Dealer Limits (edit)' },
  { value: 'limit_setup', label: 'Setup: Counterparty Limits (edit)' }
];

const pageLabel = (value) => PAGE_OPTIONS.find((p) => p.value === value)?.label || value;
const tierLabel = (value) => MIDDLE_OFFICE_ROLES.find((r) => r.value === value)?.label || value;

export default function MiddleOfficeAccessPanel() {
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [tier, setTier] = useState('middle_office_user');
  const [allowedPages, setAllowedPages] = useState([]);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fetchAssignments = () => {
    fetch(`${API_BASE_URL}/authorizers`, { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => setAssignments((data.assignments || []).filter((a) => String(a.role || '').startsWith('middle_office_'))));
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/users`, { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => setUsers(Array.isArray(data) ? data : (data.users || [])));
    fetchAssignments();
  }, []);

  useEffect(() => {
    if (!successMsg && !errorMsg) return undefined;
    const t = setTimeout(() => {
      setSuccessMsg('');
      setErrorMsg('');
    }, 4000);
    return () => clearTimeout(t);
  }, [successMsg, errorMsg]);

  const togglePage = (value) => {
    setAllowedPages((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedUser || allowedPages.length === 0) {
      setErrorMsg('Select a user and at least one page.');
      return;
    }
    const payload = {
      user_id: selectedUser,
      role: tier,
      per_deal_limit: 0,
      per_day_limit: 0,
      allowed_pages: allowedPages
    };
    const res = await fetch(`${API_BASE_URL}/authorizers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      setSuccessMsg('Middle Office access saved!');
      setErrorMsg('');
      fetchAssignments();
    } else {
      setErrorMsg('Failed to save assignment.');
    }
  };

  return (
    <div className="middle-office-page ui-page">
      <p className="page-intro">
        Assign users to a Middle Office tier (User / Officer / Manager) and choose which reports and
        setup pages they can see. Counterparty Master and Dealer Limits setup are exclusive to Middle
        Office tiers — they are no longer grantable through the general user tab-access panel.
      </p>

      {successMsg && <div className="success-message">{successMsg}</div>}
      {errorMsg && <div className="error-message">{errorMsg}</div>}

      <form className="master-form" onSubmit={handleAssign}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="mo-user">User <span className="required">*</span></label>
            <select
              id="mo-user"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              required
            >
              <option value="">Select User</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="mo-tier">Tier <span className="required">*</span></label>
            <select id="mo-tier" value={tier} onChange={(e) => setTier(e.target.value)}>
              {MIDDLE_OFFICE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Allowed Pages <span className="required">*</span></label>
          <div className="checkbox-group">
            {PAGE_OPTIONS.map((page) => (
              <div key={page.value} className="checkbox-item">
                <input
                  type="checkbox"
                  id={`mo-page-${page.value}`}
                  checked={allowedPages.includes(page.value)}
                  onChange={() => togglePage(page.value)}
                />
                <label htmlFor={`mo-page-${page.value}`}>{page.label}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn">Assign Middle Office Access</button>
        </div>
      </form>

      <div className="search-section">
        <h3>Current Middle Office Assignments</h3>
        <table className="master-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Tier</th>
              <th>Pages</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 && (
              <tr className="empty-row">
                <td colSpan="3">No Middle Office assignments yet.</td>
              </tr>
            )}
            {assignments.map((a) => (
              <tr key={a.id}>
                <td>{users.find((u) => u.id === a.user_id)?.username || a.user_id}</td>
                <td>
                  <span className="mo-status mo-status--tier">{tierLabel(a.role)}</span>
                </td>
                <td>{(a.allowed_pages || []).map(pageLabel).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
